import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Alert, Button, Card, CardBody, Col, Container, Row, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { AvForm } from 'availity-reactstrap-validation';
import { IRootState } from 'app/shared/reducers';
import { IPatientCase } from 'app/shared/model/patient-case.model';
import { IJourney } from 'app/shared/model/journey.model';
import MmamaEntityFormGroup from 'app/shared/components/mmama-entity-form/mmama-entity-form-group';
import { IMmamaEntityMaster } from 'app/shared/model/mmama-entity-master.model';
import { IMmamaEntityDto } from 'app/shared/model/mmama-entity-dto.model';
import { getMasterEntitiesByGroupNames } from 'app/entities/mmama-entity/mmama-entity.reducer';
import {
  createPatientFeedback,
  updatePatientFeedback,
  getPatientFeedbackByPatientCaseId,
  reset as resetPatientFeedback
} from 'app/entities/patient-feedback/patient-feedback.reducer';

interface IFeedbackFormProps extends StateProps, DispatchProps {
  patientCase: IPatientCase;
  journey: IJourney;
  isOpen: boolean;
  onClose: () => void;
  countrySettings?: any;
}

const FeedbackForm = (props: IFeedbackFormProps) => {
  const { patientCase, journey, isOpen, onClose, patientFeedback, countrySettings } = props;

  const [patientFeedbackMasterEntities, setPatientFeedbackMasterEntities] = useState<IMmamaEntityMaster[]>([]);
  const [patientEntityFormSelectedValues, setPatientEntityFormSelectedValues] = useState<IMmamaEntityDto[]>([]);
  const [isNew, setIsNew] = useState(true);
  const [initialFormValues, setInitialFormValues] = useState<IMmamaEntityDto[]>([]);
  const [missingMandatory, setMissingMandatory] = useState<string[]>([]);
  const [showValidation, setShowValidation] = useState(false);

  const MMAMA_ENTITY_PATIENT_FEEDBACK_GROUP = 'PATIENT_FEEDBACK';

  useEffect(() => {
    if (patientCase?.patient?.countryId) {
      // Get entities for feedback forms
      props.getMasterEntitiesByGroupNames(
        [MMAMA_ENTITY_PATIENT_FEEDBACK_GROUP],
        patientCase.patient.countryId
      );
    }
  }, [patientCase?.patient?.countryId]);

  // Fetch existing feedback when modal opens
  useEffect(() => {
    if (isOpen && patientCase?.id) {
      props.getPatientFeedbackByPatientCaseId(patientCase.id);
    }
  }, [isOpen, patientCase?.id]);

  // Update entities when store data changes
  useEffect(() => {
    setPatientFeedbackMasterEntities(props.mmamaEntitiesByGroup[MMAMA_ENTITY_PATIENT_FEEDBACK_GROUP] || []);
  }, [props.mmamaEntitiesByGroup]);

  const setEntityValues = (feedbackData) => {
    if (feedbackData && Object.keys(feedbackData).length > 0 && patientFeedbackMasterEntities && patientFeedbackMasterEntities.length > 0) {
      if (feedbackData.patientFeedbackEntities) {
        setInitialFormValues(feedbackData.patientFeedbackEntities);
      }
    }
  };
  // Handle existing feedback data
  useEffect(() => {
    if (patientFeedback && patientFeedback !== null && Object.keys(patientFeedback).length > 0) {
      setIsNew(false);

      // Set entity values for existing feedback
      setEntityValues(patientFeedback);
    } else {
      // patientFeedback is null (no feedback exists) or empty, treat as new
      setIsNew(true);
      setInitialFormValues([]);
    }
  }, [patientFeedback, patientFeedbackMasterEntities]);

  // Handle feedback creation success
  useEffect(() => {
    if (props.patientFeedbackUpdateSuccess) {
      // Reset form state
      setInitialFormValues([]);
      setIsNew(true);
      setPatientEntityFormSelectedValues([]);
      setShowValidation(false);

      onClose();
      props.resetPatientFeedback();
    }
  }, [props.patientFeedbackUpdateSuccess]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setInitialFormValues([]);
      setIsNew(true);
      setPatientEntityFormSelectedValues([]);
      setShowValidation(false);
      props.resetPatientFeedback();
    }
  }, [isOpen]);

  // Handle errors
  useEffect(() => {
    if (props.patientFeedbackErrorMessage) {
      // Determine if this is a load error or save error
      const serverMessage = props.patientFeedbackErrorMessage?.response?.data?.detail;
      const errorMessage = props.patientFeedbackUpdating 
        ? (serverMessage || 'Failed to save feedback. Please try again.')
        : 'Failed to load existing feedback. You can still create new feedback.';
      
      alert(errorMessage);
      props.resetPatientFeedback();
    }
  }, [props.patientFeedbackErrorMessage, props.patientFeedbackUpdating]);

  const handleSaveFeedback = () => {
    // Check if feedback is enabled
    if (!countrySettings?.enableDispatcherFeedback) {
      alert('Dispatcher feedback is not enabled for this country.');
      return;
    }

    // Validate that we have form entities available
    if (!patientFeedbackMasterEntities || patientFeedbackMasterEntities.length === 0) {
      alert('Feedback forms are not available. Please try again later.');
      return;
    }
    
    if (missingMandatory.length > 0) {
      setShowValidation(true);
      return;
    }

    const feedbackData = {
      patientCaseId: patientCase.id,
      patientFeedbackEntityIds: patientEntityFormSelectedValues || [],
    };

    if (!isNew && patientFeedback && patientFeedback.id) {
      // Update existing feedback
      props.updatePatientFeedback({
        ...feedbackData,
        id: patientFeedback.id
      });
    } else {
      // Create new feedback
      props.createPatientFeedback(feedbackData);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      style={{ maxWidth: '80%', width: '80%' }}
      centered
    >
      <ModalHeader toggle={onClose}>
        {isNew ? 'Add Feedback' : 'Edit Feedback'}
      </ModalHeader>
      <ModalBody>
        <Container fluid>
          <Row>
            <Col md={12}>
              {props.mmamaEntityLoading || props.patientFeedbackLoading ? (
                <div className="text-center py-4">
                  <div>Loading feedback forms...</div>
                </div>
              ) : (
                <>
                  <AvForm onValidSubmit={handleSaveFeedback} onInvalidSubmit={() => setShowValidation(true)}>
                    {/* Patient Feedback Form */}
                    {patientFeedbackMasterEntities && patientFeedbackMasterEntities.length > 0 ? (
                      <div className="mb-4">
                        <MmamaEntityFormGroup
                          prefix="patient-feedback"
                          entities={patientFeedbackMasterEntities}
                          initialValues={initialFormValues || []}
                          onChange={setPatientEntityFormSelectedValues}
                          mergeAndSort={true}
                          onValidationChange={setMissingMandatory}
                          showValidation={showValidation}
                        />
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <div>No feedback forms available for this country.</div>
                      </div>
                    )}

                    {showValidation && missingMandatory.length > 0 && (
                      <Alert color="danger">
                        Please complete the required fields: {missingMandatory.join(', ')}
                      </Alert>
                    )}

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end mt-4">
                      <Button color="secondary" className="me-2 mr-2" onClick={onClose} disabled={props.patientFeedbackUpdating}>
                        Cancel
                      </Button>
                      <Button
                        color="primary"
                        type="submit"
                        disabled={props.patientFeedbackUpdating || !patientFeedbackMasterEntities || patientFeedbackMasterEntities.length === 0}
                      >
                        {props.patientFeedbackUpdating ? 'Saving...' : (isNew ? 'Save Feedback' : 'Update Feedback')}
                      </Button>
                    </div>
                  </AvForm>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </ModalBody>
    </Modal>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  mmamaEntitiesByGroup: storeState.mmamaEntity.masterEntitiesByGroup,
  mmamaEntityLoading: storeState.mmamaEntity.loading,
  patientFeedback: storeState.patientFeedback.entity,
  patientFeedbackLoading: storeState.patientFeedback.loading,
  patientFeedbackUpdating: storeState.patientFeedback.updating,
  patientFeedbackUpdateSuccess: storeState.patientFeedback.updateSuccess,
  patientFeedbackErrorMessage: storeState.patientFeedback.errorMessage,
});

const mapDispatchToProps = {
  getMasterEntitiesByGroupNames,
  createPatientFeedback,
  updatePatientFeedback,
  getPatientFeedbackByPatientCaseId,
  resetPatientFeedback,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(FeedbackForm);
